import {
  Badge,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Spinner,
  Typography,
  useToast,
  WithToolTip,
} from "@atlas/core";
import { hasAuthToken, useTranslation } from "@atlas/sdk";
import { useAcceptApplicationTerms } from "@query";
import { APP_BASE_PATH } from "@shared";
import { ExternalLink } from "lucide-react";
import { memo, useState } from "react";
import {
  type TermsAcceptancePayload,
  TermsAndConditionsDialog,
} from "../termsAndConditions/TermsAndConditionsDialog";

interface IApplicationLaunchCardProps {
  title: string;
  description?: string;
  launchUrl: string;
  appMapId?: string;
  launchDisabled: boolean;
  isLaunching?: boolean;
  onLaunchApp: (
    launchUrl: string,
    solutionId?: string,
    appMapId?: string,
  ) => void;
  termsAccepted: boolean;
  termsId: string;
  applicationId: string;
  companyUuid: string | undefined;
  userType: string | undefined;
  userName: string;
  userEmail: string;
  companyName: string;
  isInternalUser: boolean;
  isLiteApplication?: boolean;
  isIRMDomain: boolean;
  isTermsRequired: boolean;
  label?: string;
}

export const ApplicationLaunchCard = memo(function ApplicationLaunchCard({
  title,
  description,
  launchUrl,
  appMapId,
  launchDisabled,
  isLaunching,
  onLaunchApp,
  termsAccepted,
  termsId,
  applicationId,
  companyUuid,
  userType,
  userName,
  userEmail,
  companyName,
  isInternalUser,
  isLiteApplication,
  isIRMDomain,
  isTermsRequired,
}: IApplicationLaunchCardProps) {
  const { t } = useTranslation();
  const trimmedDescription = description?.trim() ?? "";

  const toast = useToast();
  const { mutateAsync: acceptTerms, isPending: isAcceptingTerms } =
    useAcceptApplicationTerms(companyUuid, applicationId, APP_BASE_PATH);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleAcceptTerms = async (payload: TermsAcceptancePayload) => {
    if (!termsId) {
      toast.add({
        title: t("hub.terms.accept_error", {
          defaultValue: "Terms acceptance failed",
        }),
        description: t("hub.terms.missing_terms_id", {
          defaultValue: "Missing terms ID.",
        }),
        data: { type: "error" },
      });
      return;
    }

    try {
      await acceptTerms({
        termsId,
        signature: payload.signature,
      });
      setDialogOpen(false);
      onLaunchApp(launchUrl, undefined, appMapId);
    } catch (err) {
      console.error("Terms acceptance failed:", err);
      toast.add({
        title: t("hub.terms.accept_error", {
          defaultValue: "Terms acceptance failed",
        }),
        description:
          err instanceof Error
            ? err.message
            : t("hub.terms.accept_error_description", {
                defaultValue: "Unable to accept terms. Please try again.",
              }),
        data: { type: "error" },
      });
    }
  };

  return (
    <Card className="h-full min-w-0 overflow-hidden">
      <CardHeader className="min-w-0">
        <WithToolTip tooltipText={title} className="min-w-0 flex-1">
          <CardTitle className="truncate">{title}</CardTitle>
        </WithToolTip>
      </CardHeader>

      <CardContent className="flex flex-1 flex-col gap-2.5">
        {trimmedDescription ? (
          <WithToolTip
            tooltipText={trimmedDescription}
            className="w-full min-w-0">
            <Typography.P className="mt-0 mb-0 line-clamp-2 min-h-10 text-sm leading-5 text-foreground">
              {trimmedDescription}
            </Typography.P>
          </WithToolTip>
        ) : (
          <Typography.P className="mt-0 mb-0 line-clamp-2 min-h-10 text-sm leading-5 text-foreground">
            {"\u00A0"}
          </Typography.P>
        )}

        {/* Reserve the same vertical space as the solution "by" row. */}
        <div className="min-h-5" aria-hidden />
      </CardContent>

      <CardFooter className="justify-between gap-3">
        <Badge variant="secondary" size="sm">
          {t("hub.home.application_type_badge", {
            defaultValue: "Application",
          })}
        </Badge>

        <Button
          size="xs"
          variant="default"
          disabled={launchDisabled && !isLaunching}
          loading={isLaunching}
          onClick={(event) => {
            event.preventDefault();
            if (!hasAuthToken()) {
              window.dispatchEvent(new CustomEvent("session-expired"));
              return;
            }
            if (
              !isTermsRequired ||
              isInternalUser ||
              isLiteApplication ||
              isIRMDomain ||
              termsAccepted
            ) {
              onLaunchApp(launchUrl, undefined, appMapId);
              return;
            }

            if (userType === "COMPANY_ADMIN") {
              setDialogOpen(true);
              return;
            }
            toast.add({
              title: t("hub.terms.access_denied_title", {
                defaultValue: "Access Denied",
              }),
              description: t("hub.terms.access_denied_description", {
                defaultValue:
                  "Your company administrator needs to review and accept the Terms and Conditions before other users can launch the application. Please reach out to your admin.",
              }),
              data: { type: "error" },
            });
          }}>
          {t("hub.launch.launch_button", { defaultValue: "Launch" })}
          {isLaunching ? (
            <Spinner className="size-3.5 text-current" />
          ) : (
            <ExternalLink />
          )}
        </Button>
      </CardFooter>
      <TermsAndConditionsDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onAccept={handleAcceptTerms}
        userName={userName}
        userEmail={userEmail}
        companyName={companyName}
        isAccepting={isAcceptingTerms}
      />
    </Card>
  );
});
